import { useState, useMemo } from "react";
import { useLingui } from "@lingui/react/macro";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/api/api";
import type { Recipe } from "@/types/recipe";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { recipeRequests, type ScopeType } from "@/lib/api/requests";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search,
  ArrowUpDown,
  Pencil,
  Building2,
  Globe,
  MapPin,
  Plus,
  Minus,
  Check,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { Region } from "@/types/region";

interface AvailabilityTabProps {
  recipe: Recipe;
  readOnly?: boolean;
  /** Offered on the read-only view; in edit mode the toggles are the editing. */
  onEdit?: () => void;
}

interface OrgGroup {
  id: number;
  name: string;
  regions: Region[];
}

function AvailabilityToggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 hover:opacity-90",
        checked ? "justify-end bg-(--color-status-active)" : "justify-start bg-(--color-danger)/60",
      )}
    >
      <span className="flex size-5 items-center justify-center rounded-full bg-(--color-surface-content) shadow-sm">
        {checked ? (
          <Check className="size-3 text-(--color-status-active)" strokeWidth={3} />
        ) : (
          <X className="size-3 text-(--color-danger-text)/60" strokeWidth={3} />
        )}
      </span>
    </Button>
  );
}

function CollapseToggle({
  expanded,
  onClick,
}: {
  expanded: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      variant="outline"
      size="icon"
      onClick={onClick}
      className="size-6 shrink-0 rounded-full border-(--color-border) text-(--color-text-secondary) hover:bg-transparent hover:text-(--color-text-primary)"
    >
      {expanded ? <Minus className="size-3" /> : <Plus className="size-3" />}
    </Button>
  );
}

export function AvailabilityTab({ recipe, readOnly = false, onEdit }: AvailabilityTabProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [collapsedCompanies, setCollapsedCompanies] = useState<Set<number>>(new Set());
  const [expandedRegions, setExpandedRegions] = useState<Set<number>>(new Set());

  const { data: scopes, isLoading: loadingScopes } = useQuery({
    queryKey: ["recipe-scopes", recipe.id],
    queryFn: () => recipeRequests.getScopes(recipe.id),
  });

  const { data: regionsResponse, isLoading: loadingAvailability } = useQuery({
    queryKey: ["recipe-availability", recipe.id],
    queryFn: () =>
      recipeRequests.getAvailability(recipe.id, { include: ["locations"] }),
  });

  const upsertMutation = useMutation({
    mutationFn: (variables: {
      scopeType: ScopeType;
      scopeId: number;
      isExcluded: boolean;
    }) => recipeRequests.upsertScope(recipe.id, variables),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["recipe-scopes", recipe.id] });
      toast.success(t`Availability saved`);
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t`Failed to save availability`)),
  });

  const deleteMutation = useMutation({
    mutationFn: (variables: { scopeType: ScopeType; scopeId: number }) =>
      recipeRequests.deleteScope(
        recipe.id,
        variables.scopeType,
        variables.scopeId,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["recipe-scopes", recipe.id] });
      toast.success(t`Availability saved`);
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t`Failed to save availability`)),
  });

  const isPending = upsertMutation.isPending || deleteMutation.isPending;

  function isAvailable(scopeType: ScopeType, scopeId: number): boolean {
    const scope = scopes?.find(
      (s) => s.scopeType === scopeType && s.scopeId === scopeId,
    );
    return !scope?.isExcluded;
  }

  function handleToggle(
    scopeType: ScopeType,
    scopeId: number,
    currentlyAvailable: boolean,
  ) {
    if (currentlyAvailable) {
      upsertMutation.mutate({ scopeType, scopeId, isExcluded: true });
    } else {
      deleteMutation.mutate({ scopeType, scopeId });
    }
  }

  // Group regions by company
  const orgGroups = useMemo<OrgGroup[]>(() => {
    const regions = regionsResponse?.data ?? [];
    const map = new Map<number, OrgGroup>();
    for (const region of regions) {
      const companyId = region.companyId ?? region.organizationId;
      const companyName = region.company?.name ?? region.organization?.name ?? t`Unknown Concept`;
      if (!map.has(companyId)) {
        map.set(companyId, { id: companyId, name: companyName, regions: [] });
      }
      map.get(companyId)!.regions.push(region);
    }
    return Array.from(map.values());
  }, [regionsResponse, t]);


  // Filter and sort
  const filteredGroups = useMemo<OrgGroup[]>(() => {
    const q = search.toLowerCase().trim();
    return orgGroups
      .map((org) => ({
        ...org,
        regions: org.regions
          .map((r) => ({
            ...r,
            locations: (r.locations ?? [])
              .filter(
                (l) =>
                  !q ||
                  l.name.toLowerCase().includes(q) ||
                  r.name.toLowerCase().includes(q),
              )
              .sort((a, b) =>
                sortAsc
                  ? a.name.localeCompare(b.name)
                  : b.name.localeCompare(a.name),
              ),
          }))
          .filter(
            (r) =>
              !q ||
              r.name.toLowerCase().includes(q) ||
              r.locations.length > 0,
          )
          .sort((a, b) =>
            sortAsc
              ? a.name.localeCompare(b.name)
              : b.name.localeCompare(a.name),
          ),
      }))
      .filter((org) => org.regions.length > 0);
  }, [orgGroups, search, sortAsc]);

  function toggleCompany(companyId: number) {
    setCollapsedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  }

  function toggleRegion(regionId: number) {
    setExpandedRegions((prev) => {
      const next = new Set(prev);
      if (next.has(regionId)) next.delete(regionId);
      else next.add(regionId);
      return next;
    });
  }

  if (loadingScopes || loadingAvailability) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-8 w-72" />
        <div className="flex items-center justify-between">
          <Skeleton className="h-11 w-75" />
          <Skeleton className="h-11 w-24" />
        </div>
        <div className="flex flex-col gap-2 rounded-xl border bg-(--color-surface-muted) p-3">
          <Skeleton className="h-12 w-full rounded-md" />
          <Skeleton className="h-12 w-full rounded-md" />
          <Skeleton className="ml-8 h-12 w-[calc(100%-2rem)] rounded-md" />
          <Skeleton className="ml-8 h-12 w-[calc(100%-2rem)] rounded-md" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Section header */}
      <div className="flex items-center justify-between pl-2">
        <h2 className="text-2xl font-semibold leading-none text-(--color-text-primary)">
          {t`Availability by Concept and Region`}
        </h2>
        {readOnly && onEdit && (
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            className="h-10 gap-2 rounded-[10px] border-(--color-border)"
          >
            <Pencil className="size-4" />
            {t`Edit`}
          </Button>
        )}
      </div>

      {/* Search + Sort controls */}
      <div className="flex items-center justify-between">
        <div className="relative w-75">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--color-text-secondary)" />
          <Input
            placeholder={t`Search...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-11 rounded-[8px] border-(--color-border) pl-9 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-11 gap-2 rounded-[10px] border-(--color-border)"
          onClick={() => setSortAsc((v) => !v)}
        >
          <ArrowUpDown className="size-4" />
          {t`Sort`}
        </Button>
      </div>

      {/* Tree list */}
      {filteredGroups.length > 0 ? (
        <div className="flex flex-col gap-2">
          {filteredGroups.map((org) => {
            const orgExpanded = !collapsedCompanies.has(org.id);
            return (
              <div
                key={org.id}
                className="flex flex-col gap-2 rounded-xl border border-(--color-border) bg-(--color-surface-muted) p-3"
              >
                {/* Company row */}
                <div className="flex items-center gap-3">
                  <CollapseToggle
                    expanded={orgExpanded}
                    onClick={() => toggleCompany(org.id)}
                  />
                  <div className="flex flex-1 items-center gap-2 rounded-[6px] bg-(--color-surface-content) p-3">
                    <Building2 className="size-6 shrink-0 text-(--color-text-secondary)" />
                    <span className="flex-1 text-base font-medium leading-5 text-(--color-text-primary)">
                      {org.name}
                    </span>
                  </div>
                </div>

                {/* Regions */}
                {orgExpanded &&
                  org.regions.map((region) => {
                    const regionExpanded = expandedRegions.has(region.id);
                    const regionAvailable = isAvailable("REGION", region.id);
                    const hasLocations = (region.locations?.length ?? 0) > 0;

                    return (
                      <div key={region.id} className="flex flex-col gap-2 pl-9">
                        {/* Region row */}
                        <div className="flex items-center gap-3">
                          {hasLocations ? (
                            <CollapseToggle
                              expanded={regionExpanded}
                              onClick={() => toggleRegion(region.id)}
                            />
                          ) : (
                            <div className="size-6 shrink-0" />
                          )}
                          <div className="flex flex-1 items-center gap-2 rounded-[6px] bg-(--color-surface-content) p-3">
                            <Globe className="size-6 shrink-0 text-(--color-text-secondary)" />
                            <span className="flex-1 text-base font-medium leading-5 text-(--color-text-primary)">
                              {region.name}
                            </span>
                            <AvailabilityToggle
                              checked={regionAvailable}
                              onChange={() =>
                                handleToggle(
                                  "REGION",
                                  region.id,
                                  regionAvailable,
                                )
                              }
                              disabled={readOnly || isPending}
                            />
                          </div>
                        </div>

                        {/* Locations */}
                        {regionExpanded &&
                          region.locations?.map((location) => {
                            const locationAvailable = isAvailable(
                              "LOCATION",
                              location.id,
                            );
                            return (
                              <div
                                key={location.id}
                                className="flex items-center gap-3 pl-9"
                              >
                                <div className="size-6 shrink-0 opacity-0" />
                                <div className="flex flex-1 items-center gap-2 rounded-[6px] bg-(--color-surface-content) p-3">
                                  <MapPin className="size-6 shrink-0 text-(--color-text-secondary)" />
                                  <span className="flex-1 text-base font-medium leading-5 text-(--color-text-primary)">
                                    {location.name}
                                  </span>
                                  <AvailabilityToggle
                                    checked={locationAvailable}
                                    onChange={() =>
                                      handleToggle(
                                        "LOCATION",
                                        location.id,
                                        locationAvailable,
                                      )
                                    }
                                    disabled={readOnly || isPending || !regionAvailable}
                                  />
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed py-12">
          <p className="text-sm text-(--color-text-secondary)">
            {search
              ? t`No results found for "${search}"`
              : t`No regions found for this concept.`}
          </p>
        </div>
      )}
    </div>
  );
}
